/* ============================================================
   Ring — the signature component, ported from app/src/components/Ring.tsx:
   a progress arc, optional fill disc + checkmark that draws on completion.
   CSS transitions in place of Reanimated (tokens.css → .hs-ring). On desktop
   a ring you can click glows in its hue under the pointer — the one hover
   affordance a phone can never give, and the hint that this target is live.
   ============================================================ */
import React from 'react';
import type { HueName } from '@habishine/client-core';

import { hueVar } from '../theme/hues';

const VB = 64;
const CHK_LEN = 28;

export function Ring({
  size, stroke, hue, pct, check = false, track = 'tint', className = '',
}: {
  size: number; stroke: number; hue: HueName; pct: number; check?: boolean; track?: 'tint' | 'sunk'; className?: string;
}) {
  const sw = (stroke * VB) / size;
  const R = (VB - sw) / 2;
  const circ = 2 * Math.PI * R;
  const chkW = (3.2 * VB) / size;
  const complete = pct >= 1;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${VB} ${VB}`} className={`hs-ring ${className}`} aria-hidden>
      {/* the track: the pale tint on light, the bright hue at low opacity on dark (see tokens.css .ring-track) */}
      <circle cx={32} cy={32} r={R} fill="none" strokeWidth={sw} className="ring-track"
        style={{
          ['--track-t' as string]: track === 'sunk' ? 'var(--card-sunk)' : hueVar(hue, 't'),
          ['--track-c' as string]: track === 'sunk' ? 'var(--card-sunk)' : hueVar(hue, 'c'),
        }} />
      <circle cx={32} cy={32} r={R} fill="none" stroke={hueVar(hue, 'c')} strokeWidth={sw} strokeLinecap="round"
        transform="rotate(-90 32 32)" strokeDasharray={circ} strokeDashoffset={circ * (1 - Math.min(1, Math.max(0, pct)))} className="ring-fill" />
      {check ? (
        <>
          <circle cx={32} cy={32} r={R - sw / 2} fill={hueVar(hue, 'k')} className="ring-disc" style={{ opacity: complete ? 1 : 0 }} />
          <path d="M23 32.5l6 6 12-13" fill="none" stroke="#fff" strokeWidth={chkW} strokeLinecap="round" strokeLinejoin="round"
            strokeDasharray={CHK_LEN} strokeDashoffset={complete ? 0 : CHK_LEN} className="ring-chk" />
        </>
      ) : null}
    </svg>
  );
}

/** Ring + a rounded tile in the centre (habits). The done tile is the hue's
   saturated fill with a soft shadow of itself, so a finished habit glows a
   little on the card. */
export function HabitRing({ hue, pct, emoji, done, size = 64 }: { hue: HueName; pct: number; emoji: string; done: boolean; size?: number }) {
  const tile = Math.round(size * 0.69);
  return (
    <div className="relative flex shrink-0 items-center justify-center" style={{ width: size, height: size }}>
      <div className="absolute inset-0"><Ring size={size} stroke={Math.round(size / 10.6)} hue={hue} pct={pct} /></div>
      <div
        className="relative z-[1] flex items-center justify-center rounded-tile border transition-[background-color,box-shadow,border-color] duration-300"
        style={{
          width: tile, height: tile, fontSize: Math.round(size * 0.34),
          background: done ? hueVar(hue, 'k') : hueVar(hue, 't'),
          borderColor: done ? hueVar(hue, 'k') : 'var(--hairline)',
          boxShadow: done ? `0 6px 16px -6px ${hueVar(hue, 'k')}` : 'none',
        }}
      >
        {done ? <span className="font-bold text-white">✓</span> : <span>{emoji || '✦'}</span>}
      </div>
    </div>
  );
}

/** A ring you click: plays the press-dip → pop, glows in its hue on hover. */
export function RingButton({ children, onClick, label, hue, className = '' }: { children: React.ReactNode; onClick: () => void; label: string; hue?: HueName; className?: string }) {
  const [pop, setPop] = React.useState(0);
  return (
    <button
      type="button"
      aria-label={label}
      onClick={(e) => { e.stopPropagation(); setPop((n) => n + 1); onClick(); }}
      className={`ringbtn shrink-0 rounded-full ${className}`}
      key={pop}
      style={{
        ...(hue ? { ['--ring-glow' as string]: `color-mix(in srgb, ${hueVar(hue, 'c')} 55%, transparent)` } : null),
        ...(pop ? { animation: 'ringpop .3s var(--ease-spring)' } : null),
      }}
    >
      {children}
    </button>
  );
}
